import { deriveCapacityStates, getRegistrationTeamsForDivision } from './waitlist';

interface TeamLike {
  _id: string;
  status?: string;
  hasPaid?: boolean;
  created: string | Date;
  promotedAt?: string | Date;
  division?: any;
  requestedDivision?: any;
  registration?: any;
}

interface PositionResult {
  position: number;
  total: number;
}

interface PositionOpts {
  paymentRequired?: boolean;
  /**
   * Division (with `config.capacity`) — required to derive capacity state.
   * If omitted, an unlimited division is assumed (every team is secured).
   */
  division?: any;
  getDivisionId?: (t: any) => string;
  getRegistrationId?: (t: any) => string;
}

const defaultGetDivisionId = (t: TeamLike) => t.division?._id || t.division;
const defaultGetRegistrationId = (t: TeamLike) => t.registration?._id || t.registration;

/**
 * Get a team's position among teams holding a spot in a division
 * (capacity state = 'secured'). Sort: paid teams first (if payment
 * required), then by effective entry time ascending.
 */
export function getTeamPosition(
  teams: TeamLike[],
  teamId: string,
  divisionId: string,
  registrationId: string,
  opts: PositionOpts = {},
): PositionResult | null {
  const getDivId = opts.getDivisionId || defaultGetDivisionId;
  const getRegId = opts.getRegistrationId || defaultGetRegistrationId;

  const inDivision = getRegistrationTeamsForDivision(divisionId, teams);
  const states = deriveCapacityStates(inDivision, opts.division);

  const secured = inDivision.filter((t) => {
    const tRegId = getRegId(t);
    return states.get(String(t._id)) === 'secured'
      && String(tRegId) === String(registrationId);
  });

  const sorted = [...secured].sort((a, b) => {
    if (opts.paymentRequired) {
      const aPaid = a.hasPaid ? 0 : 1;
      const bPaid = b.hasPaid ? 0 : 1;
      if (aPaid !== bPaid) return aPaid - bPaid;
    }
    return new Date(a.created).getTime() - new Date(b.created).getTime();
  });

  const idx = sorted.findIndex((t) => String(t._id) === String(teamId));
  if (idx === -1) return null;

  return { position: idx + 1, total: sorted.length };
}

/**
 * Get a waitlisted team's position in the waitlist for its division.
 * Order is FIFO by effective entry time (`promotedAt ?? created`) — first
 * waitlisted = first promoted.
 */
export function getWaitlistPosition(
  teams: TeamLike[],
  teamId: string,
  divisionId: string,
  registrationId: string,
  opts: Pick<PositionOpts, 'division' | 'getDivisionId' | 'getRegistrationId'> = {},
): PositionResult | null {
  const getDivId = opts.getDivisionId || defaultGetDivisionId;
  const getRegId = opts.getRegistrationId || defaultGetRegistrationId;

  const inDivision = getRegistrationTeamsForDivision(divisionId, teams);
  const states = deriveCapacityStates(inDivision, opts.division);

  const waitlisted = inDivision.filter((t) => {
    const tRegId = getRegId(t);
    return states.get(String(t._id)) === 'waitlisted'
      && String(tRegId) === String(registrationId);
  });

  const sorted = [...waitlisted].sort((a, b) => {
    const aT = new Date(a.promotedAt || a.created).getTime();
    const bT = new Date(b.promotedAt || b.created).getTime();
    return aT - bT;
  });

  const idx = sorted.findIndex((t) => String(t._id) === String(teamId));
  if (idx === -1) return null;

  return { position: idx + 1, total: sorted.length };
}
